import * as React from "react";
import { Trans, t } from "@lingui/macro";
import { I18n } from "@lingui/react";
import styled from "styled-components";
import {
  Back,
  Button,
  Markdown,
  DisplayMedium,
  Thirds,
  Center,
  Left,
  Right,
  spacing,
} from "@conserveit/blocks";
import { history } from "../../browserHistory";
import {
  ContentContainer,
  Half,
  FieldHeading,
  FieldSection,
  FieldLabel,
  FieldBody,
  GoBack,
} from "./Action.styles";
import EquipListField from "./components/EquipListField";
import { ImagePreview } from "./components/ImagePreview";
import { CommentsColumn } from "./components/CommentsColumn";

const hasAnySavings = (action: any /* TODO: Serialised from redux */) =>
  Boolean(
    action.totalFinancialImpact ||
      action.elec ||
      action.gas ||
      action.water ||
      action.operationalSavings
  );

const StyledThirds = styled(Thirds)`
  margin-bottom: ${spacing.default};
`;

interface Props {
  action: any; // TODO: Serialised from redux
  equips: any; // @TODO
  comments: any; // @TODO
  userAccess: Insite.API.User["access"];
  isCommentsLoading: boolean;
  urlHistory: Insite.AppState["urlHistory"];
}

export const ViewAction: React.FunctionComponent<Props> = ({
  action,
  equips,
  comments,
  userAccess,
  isCommentsLoading,
  urlHistory,
}) => (
  <React.Fragment>
    <StyledThirds>
      <Left>
        <GoBack to={urlHistory.actionsHistory.path}>
          <Back text={<Trans id="action_form.back_to_actions">Back</Trans>} />
        </GoBack>
      </Left>
      <Center>
        <DisplayMedium>
          <Trans id="action_form.action">Action</Trans> {action.key}
        </DisplayMedium>
      </Center>
      <Right>
        <Button
          primary
          disabled={!userAccess.includes("editIssue")}
          thin
          onClick={() => history.push(`/actions/${action.key}/edit`)}
        >
          Edit
        </Button>
      </Right>
    </StyledThirds>
    <ContentContainer>
      <I18n>
        {({ i18n }) => (
          <Half>
            <FieldSection>
              <FieldHeading>
                {i18n._(
                  t("action_form.action_details_heading")`Action details`
                )}
              </FieldHeading>

              <FieldLabel>
                <Trans id="action_form.site">Site</Trans>
              </FieldLabel>
              <FieldBody>{action.components}</FieldBody>

              <FieldLabel>
                <Trans id="action_form.summary_label">Summary</Trans>
              </FieldLabel>
              <FieldBody>{action.summary}</FieldBody>

              {action.description && (
                <React.Fragment>
                  <FieldLabel>
                    <Trans id="action_form.description_label">
                      Description
                    </Trans>
                  </FieldLabel>
                  <Markdown>{action.description}</Markdown>
                </React.Fragment>
              )}
            </FieldSection>

            <FieldSection>
              <FieldHeading>
                {i18n._(t("action_form.status_heading")`Status`)}
              </FieldHeading>

              <FieldLabel>
                <Trans id="action_form.status_label">Action status</Trans>
              </FieldLabel>
              <FieldBody>{action.bonfire_status}</FieldBody>

              <FieldLabel>
                <Trans id="action_form.priority_label">Priority</Trans>
              </FieldLabel>
              <FieldBody>{action.priority}</FieldBody>

              <FieldLabel>
                <Trans id="action_form.context_label">Context</Trans>
              </FieldLabel>
              <FieldBody>{action.context}</FieldBody>

              <FieldLabel>
                <Trans id="action_form.responsible_label">Responsible</Trans>
              </FieldLabel>
              <FieldBody>{action.responsible}</FieldBody>

              <FieldLabel>
                <Trans id="action_form.assignee_label">Assignee</Trans>
              </FieldLabel>
              <FieldBody>{action.assignee}</FieldBody>
            </FieldSection>

            {(action.rule || equips.length > 0) && (
              <FieldSection>
                <FieldHeading>
                  {i18n._(t("action_form.rule_heading")`Rules and equips`)}
                </FieldHeading>

                <FieldLabel>
                  <Trans id="action_form.rule_label">Rule</Trans>
                </FieldLabel>
                <FieldBody>{action.rule}</FieldBody>

                {equips.length > 0 && <EquipListField equips={equips} />}
              </FieldSection>
            )}

            {(action.skysparkLink || action.screenshotLink) && (
              <FieldSection>
                <FieldHeading>
                  {i18n._(t("action_form.resources_heading")`Other resources`)}
                </FieldHeading>
                {action.skysparkLink && (
                  <React.Fragment>
                    <FieldLabel>
                      <Trans id="action_form.report_url">Report URL</Trans>
                    </FieldLabel>
                    <FieldBody>
                      <a href={action.skysparkLink} target="_blank">
                        Trend Graph
                      </a>
                    </FieldBody>
                  </React.Fragment>
                )}

                {action.screenshotLink && (
                  <React.Fragment>
                    <FieldLabel>
                      <Trans id="action_form.screenshot">Screenshot</Trans>
                    </FieldLabel>
                    <ImagePreview src={action.screenshotLink} />
                  </React.Fragment>
                )}
              </FieldSection>
            )}

            {hasAnySavings(action) && (
              <FieldSection>
                <FieldHeading>
                  {i18n._(t("action_form.savings_heading")`Savings`)}
                </FieldHeading>

                {action.totalFinancialImpact && (
                  <React.Fragment>
                    <FieldLabel>
                      <Trans id="action_form.savings_label">
                        Estimated energy savings ($)
                      </Trans>
                    </FieldLabel>
                    <FieldBody>{action.totalFinancialImpact}</FieldBody>
                  </React.Fragment>
                )}

                {action.elec && (
                  <React.Fragment>
                    <FieldLabel>
                      <Trans id="action_form.electricity_label">
                        Electricity (kWh)
                      </Trans>
                    </FieldLabel>
                    <FieldBody>{action.elec}</FieldBody>
                  </React.Fragment>
                )}

                {action.gas && (
                  <React.Fragment>
                    <FieldLabel>
                      <Trans id="action_form.gas_label">Gas (GJ)</Trans>
                    </FieldLabel>
                    <FieldBody>{action.gas}</FieldBody>
                  </React.Fragment>
                )}

                {action.water && (
                  <React.Fragment>
                    <FieldLabel>
                      <Trans id="action_form.water_label">Water (kL)</Trans>
                    </FieldLabel>
                    <FieldBody>{action.water}</FieldBody>
                  </React.Fragment>
                )}

                {action.operationalSavings && (
                  <React.Fragment>
                    <FieldLabel>
                      <Trans id="action_form.operational_savings_label">
                        Operational savings ($)
                      </Trans>
                    </FieldLabel>
                    <FieldBody>{action.operationalSavings}</FieldBody>
                  </React.Fragment>
                )}
              </FieldSection>
            )}
          </Half>
        )}
      </I18n>

      <CommentsColumn
        comments={comments}
        action={action}
        isCommentsLoading={isCommentsLoading}
      />
    </ContentContainer>
  </React.Fragment>
);

ViewAction.defaultProps = {
  comments: [],
  equips: [],
};
